import {
	CONSOLATION_DEFAULT_VALUE,
	CONSOLATION_FINISH_VALUE_SCALE_MATCHES_PLAYOFFS,
	ConsolationFinishScaleValues,
	MAX_HEAD_START,
	PlayoffFinishScaleValues,
	SEASON_FINISH_IMPORTANCE,
	SeasonFinishScaleValues,
} from "../config"
import { IPlayer } from "../types"
import { ScaleValuesStatic } from "./scales"

export class PlayerSet {
	private players: IPlayer[] = []
	private numPlayoffTeams: number = 0
	private numConsolationTeams: number = 0

	constructor(numPlayoffTeams: number, numConsolationTeams: number) {
		this.numPlayoffTeams = numPlayoffTeams
		this.numConsolationTeams = numConsolationTeams
	}

	public add(name: string, team: string, seasonFinish?: number, playoffFinish?: number, consolation?: boolean) {
		this.players.push({
			name,
			team,
			seasonFinish,
			playoffFinish,
			consolation: Boolean(consolation),
			headStart: 0,
			time: 0,
			state: "stand",
		})
		return this
	}

	public done() {
		const numConsolationTeams = this.numConsolationTeams || this.players.length - this.numPlayoffTeams
		const numLargerBracketTeams = Math.max(this.numPlayoffTeams, numConsolationTeams)

		const seasonFinishValues = SeasonFinishScaleValues(this.players.length, 0)
		const playoffFinishValues = CONSOLATION_FINISH_VALUE_SCALE_MATCHES_PLAYOFFS
			? PlayoffFinishScaleValues(numLargerBracketTeams, numLargerBracketTeams - this.numPlayoffTeams)
			: PlayoffFinishScaleValues(this.numPlayoffTeams, 0)
		const consolationFinishValues = !this.numConsolationTeams
			? ScaleValuesStatic(numConsolationTeams, CONSOLATION_DEFAULT_VALUE)
			: CONSOLATION_FINISH_VALUE_SCALE_MATCHES_PLAYOFFS
				? ConsolationFinishScaleValues(numLargerBracketTeams, numLargerBracketTeams - numConsolationTeams)
				: ConsolationFinishScaleValues(numConsolationTeams, 0)

		// scale values so season = 50% and playoffs = 50%
		const totalSeason = Object.values(seasonFinishValues).reduce((sum, i) => sum + i)
		const totalPlayoffs =
			Object.values(playoffFinishValues).reduce((sum, i) => sum + i) +
			Object.values(consolationFinishValues).reduce((sum, i) => sum + i)
		const playoffScale = (totalSeason * (1 - SEASON_FINISH_IMPORTANCE) * 2) / totalPlayoffs

		for (let i = 0; i < playoffFinishValues.length; i++) {
			playoffFinishValues[i] *= playoffScale
		}
		for (let i = 0; i < consolationFinishValues.length; i++) {
			consolationFinishValues[i] *= playoffScale
    }

		// if teams from last season have left and there are new players
		// calculate what finishes the teams that left had
		// then evenly distribute their values across new players
		const missingSeasonValues = Array.from(new Array(this.players.length)).map((_, i) => i)
			.filter((i) => !this.players.some(({ seasonFinish }) => i + 1 === seasonFinish))
			.reduce((sum, i) => sum + seasonFinishValues[i], 0)
		const missingPlayoffValues = Array.from(new Array(this.numPlayoffTeams)).map((_, i) => i)
			.filter(
				(i) =>
					!this.players.some(({ playoffFinish, consolation }) => i + 1 === playoffFinish && !consolation)
			)
			.reduce((sum, i) => sum + playoffFinishValues[i], 0)
		const missingConsolationValues = Array.from(new Array(numConsolationTeams)).map((_, i) => i)
			.filter(
				(i) => !this.players.some(({ playoffFinish, consolation }) => i + 1 === playoffFinish && consolation)
			)
			.reduce((sum, i) => sum + consolationFinishValues[i], 0)

		const numNewPlayers = this.players.reduce(
			(sum, { seasonFinish }) => sum + (seasonFinish === undefined ? 1 : 0),
			0
		)
		const newPlayerValue =
			numNewPlayers > 0
				? (missingSeasonValues + missingPlayoffValues + missingConsolationValues) / numNewPlayers
				: 0

    console.table(Array.from(new Array(this.players.length)).map((_, i) => ({
      position: i + 1,
      seasonValue: seasonFinishValues[i],
      missingSeason: !this.players.some(({ seasonFinish }) => i + 1 === seasonFinish),
      playoffValue: playoffFinishValues[i],
      missingPlayoff: i < this.numPlayoffTeams && !this.players.some(({ playoffFinish, consolation }) => i + 1 === playoffFinish && !consolation),
      consolationValue: consolationFinishValues[i],
      missingConsolation: i < numConsolationTeams && !this.players.some(({ playoffFinish, consolation }) => i + 1 === playoffFinish && consolation),
    })))

    console.table({
      numNewPlayers,
      missingSeasonValues,
      missingPlayoffValues,
      missingConsolationValues,
      newPlayerValue,
    })

		// limit head start to a certain yard line
		const maxValue = Math.max(...seasonFinishValues) + Math.max(...playoffFinishValues, ...consolationFinishValues)

		const values = this.players
			.map(({ name, seasonFinish, playoffFinish, consolation }) => {
				if (seasonFinish !== undefined && playoffFinish !== undefined) {
					const value =
						seasonFinishValues[seasonFinish - 1] +
						(consolation
							? consolationFinishValues[playoffFinish - 1]
							: playoffFinishValues[playoffFinish - 1])
					return value
				}

				return newPlayerValue
			})
			.map((value) => (value / maxValue) * MAX_HEAD_START)

		// get the smallest value, so then all teams are reduced by it so the lowest team starts at 0
		const minValue = Math.min(...values)

		this.players.forEach((player, i) => {
			player.headStart = Math.round(values[i] - minValue)
		})

		this.players.sort((a, b) => (a.headStart > b.headStart ? -1 : 1))

		return this.players
	}
}
